function DemoResetShortcut({ businessId }) {
  const backend = window.PlummeBackend;
  const preferences = backend?.userProfile?.preferences || {};
  const available = window.PlummeDemoResetShortcut?.isAvailable({
    demoAccount: backend?.isDemoAccount,
    memberships: backend?.memberships,
    businessId,
  });
  const enabled = window.PlummeDemoResetShortcut?.isEnabled({ preferences, businessId });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!available || !enabled) return null;
  const reset = async () => {
    setBusy(true);
    try {
      const result = await backend.resetAllBusinessData(t('demoReset.reason'));
      if (result?.cleanupFailed) throw new Error(t('demoReset.failed'));
      toast(t('demoReset.complete'), 'success');
    } catch (error) {
      toast(error?.message || t('demoReset.failed'), 'error');
      throw error;
    } finally { setBusy(false); }
  };
  return <React.Fragment>
    <button type="button" className="demo-reset-shortcut" disabled={busy} onClick={() => setConfirmOpen(true)}>
      <Icon name="refresh" size={14}/><span>{t('demoReset.button')}</span>
    </button>
    {confirmOpen && <ResetAllDataConfirmModal busy={busy} onConfirm={reset} onClose={() => setConfirmOpen(false)} i18nPrefix="demoReset"/>}
  </React.Fragment>;
}

function InvoiceControl({ businessId, year, route, setRoute, children }) {
  const [open, setOpen] = useState(false);
  const workspace = useSuperPlummaWorkspaceRoute(route, setRoute);
  const routeRef = useRef(workspace.effectiveRoute); routeRef.current = workspace.effectiveRoute;
  const [registry] = useState(() => window.PlummeRegistryLiveControl.create({
    backend: () => window.PlummeBackend,
    country: () => window.PlummeBackend.appState?.settings?.country,
    isCurrent: () => window.PlummeBackend.activeBusinessId === businessId && Number(window.PlummeBackend.appState?.activeYear) === Number(year),
    getRoute: () => routeRef.current, navigate: (next) => workspace.navigate(next),
    records: (kind) => {
      const state = window.PlummeBackend.appState;
      return (state?.[`${kind}s`] || []).map(row => kind === 'product' ? mapProductToUi(row)
        : kind === 'customer' ? mapCustomerToUi(row, state) : { ...mapSupplierToUi(row, state), zip: row.postalCode || '', city: row.postalPlace || '' });
    },
  }));
  const [invoiceList] = useState(() => window.PlummeInvoiceListLiveControl.create({
    businessId, year, backend: () => window.PlummeBackend,
    isCurrent: () => window.PlummeBackend.activeBusinessId === businessId && Number(window.PlummeBackend.appState?.activeYear) === Number(year),
    getRoute: () => routeRef.current, navigate: workspace.navigate,
    assertCanNavigate: () => {
      if (registry.getSnapshot().dirty) throw new Error('registry.unsaved');
      const editor = routeRef.current === 'faktura' && window.PlummeInvoiceControl.currentEditor(businessId);
      if (editor && editor.snapshot().dirty && editor.snapshot().hasContent) throw new Error('listControl.unsaved');
      registry.close();
    },
    execute: window.PlummeInvoiceListActions.create({ backend: () => window.PlummeBackend, customerEmail: (row) => window.invoiceListCustomerEmail(row), sendPayload: (row, email) => window.invoiceListSendPayload(row, email), sendSubject: (row) => window.invoiceListSendSubject(row) }),
    interactionBlocked: () => Boolean(document.querySelector('.overlay .modal:not(.superplumma-chat-modal)')),
    pending: key => localStorage.getItem(key) === 'pending',
    markPending: key => localStorage.setItem(key, 'pending'),
    clearPending: key => localStorage.removeItem(key),
  }));
  const [pageFlow] = useState(() => window.PlummePageLiveControl.create({
    getRoute: () => routeRef.current,
    country: () => window.PlummeBackend.appState?.settings?.country,
    navigate: (next) => workspace.navigate(next),
  }));
  window.PlummePageLiveControlSession = pageFlow;
  useEffect(() => {
    window.PlummePageLiveControlSession = pageFlow;
    return () => { if (window.PlummePageLiveControlSession === pageFlow) window.PlummePageLiveControlSession = null; };
  }, [pageFlow]);
  const [appSurface] = useState(() => window.PlummeAppUiControl.create({
    getRoute: () => routeRef.current,
    isCurrent: () => window.PlummeBackend.activeBusinessId === businessId && Number(window.PlummeBackend.appState?.activeYear) === Number(year),
  }));
  const [session] = useState(() => window.PlummeInvoiceLiveControl.createSession({
    businessId, year, registry, invoiceList, pageFlow, appSurface, getRoute: () => routeRef.current,
    locale: () => PlummeI18n.getLocale(),
    translate: (key) => t(key),
    vatOptions: () => window.PlummeBackend.getSalesVatOptions(),
    highlight: (step) => window.PlummeInvoiceLiveEditor.highlight(step),
    isCurrent: () => window.PlummeBackend.activeBusinessId === businessId && Number(window.PlummeBackend.appState?.activeYear) === Number(year),
    ask: (question, options) => window.PlummeBackend.askHomeAssistant(question, options),
    navigate: (next) => workspace.navigate(next),
  }));
  window.PlummeAppControlSession = session;
  useEffect(() => {
    window.PlummeAppControlSession = session;
    return () => { if (window.PlummeAppControlSession === session) window.PlummeAppControlSession = null; };
  }, [session]);
  const [state, setState] = useState(session.getSnapshot);
  useEffect(() => session.subscribe(() => setState(session.getSnapshot())), [session]);
  useEffect(() => () => session.dispose(), [session]);
  // Keep one session mounted while its home and floating views change routes.
  const control = { session, state, workspace };
  useEffect(() => { if (state.busy) setOpen(true); }, [state.busy]);
  useEffect(() => { if (state.approval) setOpen(false); }, [state.approval?.id]);
  // Mobile: a tap outside the open panel minimizes it. A listener (not a scrim) so the bottom nav and
  // floating buttons stay tappable and still act on the same tap.
  useEffect(() => {
    if (!open) return undefined;
    const mobile = window.matchMedia('(max-width:610px)');
    const closeOnOutsideTap = (event) => {
      if (!mobile.matches || event.target.closest?.('.invoice-control, [role="dialog"], .modal')) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', closeOnOutsideTap);
    return () => document.removeEventListener('pointerdown', closeOnOutsideTap);
  }, [open]);
  return <SuperPlummaContext.Provider value={control}>
    {children}
    <McpActionApproval businessId={businessId} />
    {route !== 'superplumma' && <aside className={`invoice-control ${route === 'hjem' ? 'is-home' : ''} ${open ? 'is-open' : ''}`} aria-label={t('control.title')}>
      {open ? <SuperPlummaChat {...control} onMinimize={() => setOpen(false)} /> : <React.Fragment>
        <DemoResetShortcut businessId={businessId}/>
        <button type="button" className="invoice-control-launcher" onClick={() => setOpen(true)} aria-expanded={false} aria-label={t('control.title')} title={t('control.title')} data-help={t('helpTopics.assistantRun.body')} data-help-title={t('helpTopics.assistantRun.title')}><img src={(window.__resources && window.__resources.superPlummaHero) || 'src/img/SuperPlumma.png'} alt="" /></button>
      </React.Fragment>}
    </aside>}
    {state.approval && state.approval.presentation !== 'invoice_review' && <PageActionApproval key={state.approval.id} approval={state.approval} onClose={() => session.stop()} onConfirm={event => session.decideApproval(state.approval.id, true, event)} />}
    {state.form && <InvoiceControlRecordForm key={state.form.id} form={state.form} session={session} busy={state.busy} error={state.error ? t(state.error) : ''} />}
  </SuperPlummaContext.Provider>;
}
Object.assign(window, { InvoiceControl });
